import React, { useEffect, useMemo, useReducer, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSectionEditor } from "./SectionEditorContext";
import {
  createDropdownDefinition,
  createDropdownOption,
} from "../dropdownLibraryUtils";
import LessonRichTextEditor from "../../lessons/LessonRichTextEditor";

const TEACHER_COMMENT_HEADER = "Teacher's comment";

const createColumn = (index = 0, overrides = {}) => ({
  id: `column-${index}`,
  header: `Column ${index + 1}`,
  accessor: `col_${index + 1}`,
  direction: "rtl",
  multiline: true,
  ...overrides,
});

const createInputCell = (overrides = {}) => ({
  type: "input",
  prefill: "",
  expectedAnswer: "",
  ...overrides,
});

const createReadonlyCell = (overrides = {}) => ({
  type: "readonly",
  text: "",
  ...overrides,
});

const createDropdownCell = (overrides = {}) => ({
  type: "dropdown",
  dropdownId: "",
  correctOptionIds: [],
  ...overrides,
});

const createCell = (type = "input", overrides = {}) => {
  switch (type) {
    case "readonly":
      return createReadonlyCell(overrides);
    case "dropdown":
      return createDropdownCell(overrides);
    case "input":
    default:
      return createInputCell(overrides);
  }
};

const createRow = (columnCount = 0) =>
  Array.from({ length: columnCount }, () => createCell());

const isTeacherCommentColumn = (column) => {
  if (!column || typeof column !== "object") {
    return false;
  }

  if (column.isTeacherComment) {
    return true;
  }

  const label =
    typeof column.header === "string"
      ? column.header
      : typeof column.name === "string"
      ? column.name
      : "";

  const normalized = label.toLowerCase();
  return normalized.includes("teacher") && normalized.includes("comment");
};

const ensureTeacherCommentColumn = (columns, rows) => {
  const teacherIndex = columns.findIndex(isTeacherCommentColumn);

  if (teacherIndex === -1) {
    const teacherColumn = createColumn(columns.length, {
      id: "teacher-comment",
      header: TEACHER_COMMENT_HEADER,
      accessor: "teacher_comment",
      direction: "ltr",
      multiline: true,
      isTeacherComment: true,
    });

    return {
      columns: [...columns, teacherColumn],
      rows: rows.map((row) => [...row, createCell()]),
    };
  }

  const normalizedColumns = columns.map((column, index) => {
    if (index !== teacherIndex) {
      return column;
    }

    const multilineValue =
      column.multiline != null
        ? column.multiline
        : column.allowMultiline != null
        ? column.allowMultiline
        : true;

    return {
      ...column,
      id: column.id || "teacher-comment",
      header: column.header || column.name || TEACHER_COMMENT_HEADER,
      accessor: column.accessor || "teacher_comment",
      direction: column.direction || "ltr",
      multiline: multilineValue,
      isTeacherComment: true,
    };
  });

  let normalizedRows = rows.map((row) => {
    const next = [...row];
    while (next.length < normalizedColumns.length) {
      next.push(createCell());
    }
    return next.slice(0, normalizedColumns.length);
  });

  if (teacherIndex !== normalizedColumns.length - 1) {
    const teacherColumn = normalizedColumns[teacherIndex];
    const withoutTeacher = normalizedColumns.filter((_, idx) => idx !== teacherIndex);
    const reorderedColumns = [...withoutTeacher, teacherColumn];

    normalizedRows = normalizedRows.map((row) => {
      const teacherCell = row[teacherIndex] ?? createCell();
      const withoutTeacherCells = row.filter((_, idx) => idx !== teacherIndex);
      return [...withoutTeacherCells, teacherCell];
    });

    return { columns: reorderedColumns, rows: normalizedRows };
  }

  return { columns: normalizedColumns, rows: normalizedRows };
};

const sanitizeDropdownCell = (cell, dropdowns) => {
  if (!cell || cell.type !== "dropdown") {
    return cell;
  }

  const dropdown = dropdowns.find((item) => item.id === cell.dropdownId);
  if (!dropdown) {
    return cell;
  }

  const validIds = new Set(dropdown.options.map((option) => option.id));
  const currentIds = Array.isArray(cell.correctOptionIds)
    ? cell.correctOptionIds.filter((id) => validIds.has(id))
    : [];
  const nextIds = dropdown.allowMultiple
    ? currentIds
    : currentIds.slice(0, currentIds.length ? 1 : 0);

  if (
    !Array.isArray(cell.correctOptionIds) ||
    cell.correctOptionIds.length !== nextIds.length ||
    (!dropdown.allowMultiple && nextIds[0] !== cell.correctOptionIds?.[0])
  ) {
    return { ...cell, correctOptionIds: nextIds };
  }

  return cell;
};

const sanitizeRowsForDropdowns = (rows, dropdowns) =>
  rows.map((row) => row.map((cell) => sanitizeDropdownCell(cell, dropdowns)));

const normalizeCell = (cell) => {
  if (!cell) {
    return createCell();
  }

  if (typeof cell === "string") {
    if (!cell.trim()) {
      return createCell();
    }

    return createReadonlyCell({ text: cell });
  }

  if (typeof cell !== "object") {
    return createCell();
  }

  switch (cell.type) {
    case "readonly":
      return createReadonlyCell({ text: cell.text ?? "" });
    case "dropdown":
      return createDropdownCell({
        dropdownId: cell.dropdownId || "",
        correctOptionIds: Array.isArray(cell.correctOptionIds)
          ? cell.correctOptionIds
          : [],
      });
    case "text":
      return createInputCell({
        prefill: cell.submittedValue ?? "",
        expectedAnswer: cell.answer ?? "",
      });
    case "question":
      return createReadonlyCell({ text: cell.value ?? "" });
    case "picklist":
      return createDropdownCell({
        dropdownId: cell.dropdownId || "",
        correctOptionIds: Array.isArray(cell.correctOptionIds)
          ? cell.correctOptionIds
          : [],
      });
    case "input":
    default:
      return createInputCell({
        prefill: cell.prefill ?? "",
        expectedAnswer: cell.expectedAnswer ?? "",
      });
  }
};

export const createInitialTableState = (metadata = {}) => {
  const columns = Array.isArray(metadata.columns)
    ? metadata.columns.map((column, index) => {
        const base = {
          ...createColumn(index),
          ...(column || {}),
        };

        if (!base.header && typeof column?.name === "string") {
          base.header = column.name;
        }

        if (
          base.multiline == null &&
          typeof column?.allowMultiline === "boolean"
        ) {
          base.multiline = column.allowMultiline;
        }

        return base;
      })
    : [];

  const dropdowns = Array.isArray(metadata.dropdowns)
    ? metadata.dropdowns.map((dropdown, index) =>
        createDropdownDefinition(index, dropdown)
      )
    : [];

  const rows = Array.isArray(metadata.rows)
    ? metadata.rows.map((row) =>
        Array.isArray(row)
          ? row.map((cell) => normalizeCell(cell))
          : createRow(columns.length)
      )
    : [];

  const normalizedRows = rows.map((row) => {
    if (row.length === columns.length) {
      return row;
    }

    const next = [...row];
    while (next.length < columns.length) {
      next.push(createCell());
    }
    return next.slice(0, columns.length);
  });

  const dropdownMap = new Map(dropdowns.map((dropdown) => [dropdown.id, dropdown]));
  const rowsWithDefaults = normalizedRows.map((row) =>
    row.map((cell) => {
      if (!cell || cell.type !== "dropdown") {
        return cell;
      }

      const dropdown = dropdownMap.get(cell.dropdownId);
      if (!dropdown) {
        return cell;
      }

      if (!Array.isArray(cell.correctOptionIds) || !cell.correctOptionIds.length) {
        const defaultIds = dropdown.options
          .filter((option) => option.isCorrect)
          .map((option) => option.id);

        if (defaultIds.length) {
          const nextIds = dropdown.allowMultiple
            ? defaultIds
            : defaultIds.slice(0, 1);
          return { ...cell, correctOptionIds: nextIds };
        }
      }

      return sanitizeDropdownCell(cell, dropdowns);
    })
  );

  const { columns: columnsWithTeacher, rows: rowsWithTeacher } =
    ensureTeacherCommentColumn(columns, rowsWithDefaults);

  return {
    columns: columnsWithTeacher,
    rows: rowsWithTeacher,
    dropdowns,
    description: typeof metadata.description === "string" ? metadata.description : "",
  };
};

export const tableSectionReducer = (state, action) => {
  switch (action.type) {
    case "HYDRATE": {
      if (!action.payload || typeof action.payload !== "object") {
        return state;
      }
      const nextState = createInitialTableState(action.payload);
      const currentSerialized = JSON.stringify(state);
      const nextSerialized = JSON.stringify(nextState);
      return currentSerialized === nextSerialized ? state : nextState;
    }
    case "ADD_COLUMN": {
      const teacherIndex = state.columns.findIndex(
        (column) => column?.isTeacherComment
      );
      const newColumnIndex =
        teacherIndex === -1 ? state.columns.length : teacherIndex;
      const newColumn = {
        ...createColumn(newColumnIndex),
        ...(action.payload || {}),
      };

      if (teacherIndex === -1) {
        const nextColumns = [...state.columns, newColumn];
        const nextRows = state.rows.map((row) => [...row, createCell()]);
        return { ...state, columns: nextColumns, rows: nextRows };
      }

      const nextColumns = [
        ...state.columns.slice(0, teacherIndex),
        newColumn,
        ...state.columns.slice(teacherIndex),
      ];

      const nextRows = state.rows.map((row) => {
        const nextRow = [...row];
        nextRow.splice(teacherIndex, 0, createCell());
        return nextRow;
      });

      return { ...state, columns: nextColumns, rows: nextRows };
    }
    case "REMOVE_COLUMN": {
      const removeIndex = action.index;
      if (
        removeIndex == null ||
        removeIndex < 0 ||
        state.columns[removeIndex]?.isTeacherComment
      ) {
        return state;
      }
      const nextColumns = state.columns.filter((_, idx) => idx !== removeIndex);
      const nextRows = state.rows.map((row) =>
        row.filter((_, idx) => idx !== removeIndex)
      );
      return { ...state, columns: nextColumns, rows: nextRows };
    }
    case "UPDATE_COLUMN": {
      const { index, updates } = action;
      if (
        index == null ||
        index < 0 ||
        index >= state.columns.length ||
        !updates ||
        typeof updates !== "object"
      ) {
        return state;
      }
      const nextColumns = state.columns.map((column, idx) =>
        idx === index
          ? {
              ...column,
              ...updates,
              isTeacherComment: column.isTeacherComment,
            }
          : column
      );
      return { ...state, columns: nextColumns };
    }
    case "SET_DESCRIPTION": {
      const nextDescription = action.value ?? "";
      if (state.description === nextDescription) {
        return state;
      }

      return { ...state, description: nextDescription };
    }
    case "ADD_ROW": {
      const nextRow = createRow(state.columns.length);
      return { ...state, rows: [...state.rows, nextRow] };
    }
    case "REMOVE_ROW": {
      const removeIndex = action.index;
      if (removeIndex == null || removeIndex < 0) {
        return state;
      }
      return {
        ...state,
        rows: state.rows.filter((_, idx) => idx !== removeIndex),
      };
    }
    case "UPDATE_CELL": {
      const { rowIndex, columnIndex, updates, replace = false } = action;
      if (
        rowIndex == null ||
        columnIndex == null ||
        rowIndex < 0 ||
        columnIndex < 0 ||
        rowIndex >= state.rows.length ||
        columnIndex >= state.columns.length
      ) {
        return state;
      }
      const nextRows = state.rows.map((row, idx) => {
        if (idx !== rowIndex) {
          return row;
        }
        const nextRow = [...row];
        const currentCell = row[columnIndex] || createCell();
        const updatePayload = { ...(updates || {}) };
        if (
          Object.prototype.hasOwnProperty.call(updatePayload, "dropdownId") &&
          updatePayload.dropdownId !== currentCell.dropdownId
        ) {
          updatePayload.correctOptionIds = [];
        }
        if (replace) {
          const nextType = updatePayload?.type || "input";
          const { type: _removed, ...restUpdates } = updatePayload || {};
          nextRow[columnIndex] = createCell(nextType, restUpdates);
        } else {
          nextRow[columnIndex] = { ...currentCell, ...updatePayload };
        }
        const sanitizedCell = sanitizeDropdownCell(
          nextRow[columnIndex],
          state.dropdowns
        );
        if (sanitizedCell !== nextRow[columnIndex]) {
          nextRow[columnIndex] = sanitizedCell;
        }
        return nextRow;
      });
      return { ...state, rows: nextRows };
    }
    case "ADD_DROPDOWN": {
      const nextDropdown = createDropdownDefinition(state.dropdowns.length);
      return { ...state, dropdowns: [...state.dropdowns, nextDropdown] };
    }
    case "REMOVE_DROPDOWN": {
      const removeIndex = action.index;
      if (removeIndex == null || removeIndex < 0) {
        return state;
      }

      const dropdownToRemove = state.dropdowns[removeIndex];
      if (!dropdownToRemove) {
        return state;
      }

      const nextDropdowns = state.dropdowns.filter((_, idx) => idx !== removeIndex);
      const nextRows = state.rows.map((row) =>
        row.map((cell) =>
          cell.type === "dropdown" && cell.dropdownId === dropdownToRemove.id
            ? createCell()
            : cell
        )
      );

      return { ...state, dropdowns: nextDropdowns, rows: nextRows };
    }
    case "UPDATE_DROPDOWN": {
      const { index, updates } = action;
      if (
        index == null ||
        index < 0 ||
        index >= state.dropdowns.length ||
        !updates ||
        typeof updates !== "object"
      ) {
        return state;
      }

      const nextDropdowns = state.dropdowns.map((dropdown, idx) =>
        idx === index ? { ...dropdown, ...updates } : dropdown
      );

      const sanitizedRows = sanitizeRowsForDropdowns(state.rows, nextDropdowns);
      return { ...state, dropdowns: nextDropdowns, rows: sanitizedRows };
    }
    case "ADD_DROPDOWN_OPTION": {
      const { dropdownIndex } = action;
      if (
        dropdownIndex == null ||
        dropdownIndex < 0 ||
        dropdownIndex >= state.dropdowns.length
      ) {
        return state;
      }

      const nextDropdowns = state.dropdowns.map((dropdown, idx) => {
        if (idx !== dropdownIndex) {
          return dropdown;
        }

        const nextOptions = [
          ...dropdown.options,
          createDropdownOption(dropdown.options.length),
        ];

        return { ...dropdown, options: nextOptions };
      });

      const sanitizedRows = sanitizeRowsForDropdowns(state.rows, nextDropdowns);
      return { ...state, dropdowns: nextDropdowns, rows: sanitizedRows };
    }
    case "UPDATE_DROPDOWN_OPTION": {
      const { dropdownIndex, optionIndex, updates } = action;
      if (
        dropdownIndex == null ||
        dropdownIndex < 0 ||
        dropdownIndex >= state.dropdowns.length ||
        optionIndex == null ||
        optionIndex < 0 ||
        !updates ||
        typeof updates !== "object"
      ) {
        return state;
      }

      const dropdown = state.dropdowns[dropdownIndex];
      if (!dropdown || optionIndex >= dropdown.options.length) {
        return state;
      }

      const nextOptions = dropdown.options.map((option, idx) =>
        idx === optionIndex ? { ...option, ...updates } : option
      );

      const nextDropdowns = state.dropdowns.map((item, idx) =>
        idx === dropdownIndex ? { ...item, options: nextOptions } : item
      );

      const sanitizedRows = sanitizeRowsForDropdowns(state.rows, nextDropdowns);
      return { ...state, dropdowns: nextDropdowns, rows: sanitizedRows };
    }
    case "REMOVE_DROPDOWN_OPTION": {
      const { dropdownIndex, optionIndex } = action;
      if (
        dropdownIndex == null ||
        dropdownIndex < 0 ||
        dropdownIndex >= state.dropdowns.length ||
        optionIndex == null ||
        optionIndex < 0
      ) {
        return state;
      }

      const dropdown = state.dropdowns[dropdownIndex];
      if (!dropdown || optionIndex >= dropdown.options.length) {
        return state;
      }

      const nextOptions = dropdown.options.filter((_, idx) => idx !== optionIndex);

      const nextDropdowns = state.dropdowns.map((item, idx) =>
        idx === dropdownIndex
          ? {
              ...item,
              options: nextOptions.length ? nextOptions : [createDropdownOption()],
            }
          : item
      );

      return { ...state, dropdowns: nextDropdowns };
    }
    default:
      return state;
  }
};

const computeValidationErrors = (state) => {
  const errors = {};
  if (!state.columns.length) {
    errors.columns = "Add at least one column.";
  }

  if (state.rows.some((row) => row.length !== state.columns.length)) {
    errors.rows = "Each row must include values for every column.";
  }

  const dropdownMap = new Map(
    state.dropdowns.map((dropdown) => [dropdown.id, dropdown])
  );

  const missingDropdowns = [];
  const missingCorrectOptions = [];
  state.rows.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (cell?.type !== "dropdown") {
        return;
      }

      const dropdown = dropdownMap.get(cell.dropdownId);
      if (!dropdown) {
        missingDropdowns.push(`Row ${rowIndex + 1}, Column ${columnIndex + 1}`);
        return;
      }

      const validIds = new Set(dropdown.options.map((option) => option.id));
      const selectedIds = Array.isArray(cell.correctOptionIds)
        ? cell.correctOptionIds.filter((id) => validIds.has(id))
        : [];

      if (!selectedIds.length) {
        missingCorrectOptions.push(
          `Row ${rowIndex + 1}, Column ${columnIndex + 1}`
        );
      }
    });
  });

  if (missingDropdowns.length) {
    errors.dropdowns = `Dropdown reference missing for ${missingDropdowns.join(
      "; "
    )}.`;
  }

  if (missingCorrectOptions.length) {
    errors.dropdownCorrectOptions = `Select a correct option for dropdown cells at ${missingCorrectOptions.join(
      "; "
    )}.`;
  }

  const dropdownIssues = state.dropdowns
    .map((dropdown, index) => {
      if (!dropdown.options.length) {
        return `Dropdown ${index + 1} must include at least one option.`;
      }
      if (
        dropdown.options.some(
          (option) => !option.label || !option.label.trim()
        )
      ) {
        return `Dropdown ${index + 1} has option labels that are required.`;
      }
      return null;
    })
    .filter(Boolean);

  if (dropdownIssues.length) {
    errors.dropdownOptions = dropdownIssues;
  }

  return errors;
};

const TableSectionEditor = () => {
  const { section, updateMetadata, setSectionErrors, sectionErrors } =
    useSectionEditor();
  const [state, dispatch] = useReducer(
    tableSectionReducer,
    section.metadata,
    createInitialTableState
  );
  const serializedRef = useRef(JSON.stringify(state));
  const lastErrorRef = useRef(null);

  useEffect(() => {
    dispatch({ type: "HYDRATE", payload: section.metadata });
  }, [section.metadata]);

  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serializedRef.current !== serialized) {
      serializedRef.current = serialized;
      updateMetadata({
        columns: state.columns,
        rows: state.rows,
        dropdowns: state.dropdowns,
        description: state.description,
      });
    }
  }, [state, updateMetadata]);

  useEffect(() => {
    const errors = computeValidationErrors(state);
    const serializedErrors = JSON.stringify(errors);
    if (lastErrorRef.current !== serializedErrors) {
      lastErrorRef.current = serializedErrors;
      setSectionErrors(Object.keys(errors).length ? errors : null);
    }
  }, [state, setSectionErrors]);

  const addColumn = () => dispatch({ type: "ADD_COLUMN" });
  const addRow = () => dispatch({ type: "ADD_ROW" });

  const columnHeaders = useMemo(
    () =>
      state.columns.map((column, index) => {
        const direction = column.direction || "rtl";
        const isTeacherColumn = Boolean(column.isTeacherComment);
        const allowsMultiline = column.multiline !== false;

        return (
          <Grid item xs={12} md={6} key={column.id || `column-${index}`}>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: "background.default", height: "100%" }}
            >
              <Stack spacing={1.5} height="100%">
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label={`Column ${index + 1}`}
                    value={column.header}
                    fullWidth
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_COLUMN",
                        index,
                        updates: {
                          header: event.target.value,
                        },
                      })
                    }
                    disabled={isTeacherColumn}
                    helperText={
                      isTeacherColumn
                        ? "Label fixed for teacher feedback column."
                        : undefined
                    }
                  />
                  <IconButton
                    aria-label={`Remove column ${index + 1}`}
                    onClick={() => dispatch({ type: "REMOVE_COLUMN", index })}
                    size="small"
                    disabled={isTeacherColumn}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <TextField
                  select
                  label="Text direction"
                  value={direction}
                  onChange={(event) =>
                    dispatch({
                      type: "UPDATE_COLUMN",
                      index,
                      updates: {
                        direction: event.target.value,
                      },
                    })
                  }
                >
                  <MenuItem value="rtl">Right to left</MenuItem>
                  <MenuItem value="ltr">Left to right</MenuItem>
                </TextField>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowsMultiline}
                      onChange={(event) =>
                        dispatch({
                          type: "UPDATE_COLUMN",
                          index,
                          updates: {
                            multiline: event.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Allow multiline responses"
                />

                {isTeacherColumn && (
                  <Typography variant="caption" color="text.secondary">
                    This column is reserved for teacher feedback and is always
                    included at the end of the table.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
        );
      }),
    [state.columns]
  );

  const handleCellTypeChange = (rowIndex, columnIndex, type) => {
    dispatch({
      type: "UPDATE_CELL",
      rowIndex,
      columnIndex,
      replace: true,
      updates: { type },
    });
  };

  const dropdownOptions = useMemo(
    () =>
      state.dropdowns.map((dropdown) => ({
        value: dropdown.id,
        label: dropdown.name,
      })),
    [state.dropdowns]
  );

  const dropdownMap = useMemo(
    () => new Map(state.dropdowns.map((dropdown) => [dropdown.id, dropdown])),
    [state.dropdowns]
  );

  const getDropdownOptionLabel = (dropdown, optionId) =>
    dropdown?.options.find((option) => option.id === optionId)?.label || optionId;

  const handleCorrectOptionChange = (
    rowIndex,
    columnIndex,
    dropdown,
    value
  ) => {
    if (!dropdown) {
      return;
    }

    if (dropdown.allowMultiple) {
      const nextValue = Array.isArray(value)
        ? value.filter(Boolean)
        : [];
      dispatch({
        type: "UPDATE_CELL",
        rowIndex,
        columnIndex,
        updates: { correctOptionIds: nextValue },
      });
      return;
    }

    const nextValue = value ? [value] : [];
    dispatch({
      type: "UPDATE_CELL",
      rowIndex,
      columnIndex,
      updates: { correctOptionIds: nextValue },
    });
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.paper" }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems="flex-start"
          spacing={3}
        >
          <Box sx={{ flexGrow: 1, width: "100%" }}>
            <Stack spacing={3}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  Section description
                </Typography>
                <LessonRichTextEditor
                  value={state.description}
                  onChange={(value) =>
                    dispatch({ type: "SET_DESCRIPTION", value })
                  }
                  minHeight={320}
                />
                <Typography variant="body2" color="text.secondary">
                  Use the description to provide context, instructions, or
                  embed supporting media such as YouTube videos for your
                  learners.
                </Typography>
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2">Columns</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addColumn}
                  >
                    Add column
                  </Button>
                </Stack>
                {sectionErrors?.columns && (
                  <Typography color="error" variant="body2">
                    {sectionErrors.columns}
                  </Typography>
                )}
                {state.columns.length ? (
                  <Grid container spacing={2}>
                    {columnHeaders}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Add columns to define the table structure.
                  </Typography>
                )}
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2">Rows</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    disabled={!state.columns.length}
                  >
                    Add row
                  </Button>
                </Stack>
                {sectionErrors?.rows && (
                  <Typography color="error" variant="body2">
                    {sectionErrors.rows}
                  </Typography>
                )}
                {sectionErrors?.dropdownCorrectOptions && (
                  <Typography color="error" variant="body2">
                    {sectionErrors.dropdownCorrectOptions}
                  </Typography>
                )}
                {state.rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {state.columns.length
                      ? "Add rows to capture sample data or defaults."
                      : "Add a column before configuring rows."}
                  </Typography>
                ) : (
                  state.rows.map((row, rowIndex) => (
                    <Paper
                      key={`row-${rowIndex}`}
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "background.default" }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle2">
                            Row {rowIndex + 1}
                          </Typography>
                          <IconButton
                            aria-label={`Remove row ${rowIndex + 1}`}
                            onClick={() =>
                              dispatch({ type: "REMOVE_ROW", index: rowIndex })
                            }
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Grid container spacing={2}>
                          {state.columns.map((column, columnIndex) => {
                            const cell = row[columnIndex] || createCell();
                            const dropdown = dropdownMap.get(cell.dropdownId);
                            const selectedOptionIds = Array.isArray(
                              cell.correctOptionIds
                            )
                              ? cell.correctOptionIds
                              : [];
                            return (
                              <Grid item xs key={`${rowIndex}-${column.id}`}>
                                <Stack spacing={1.5}>
                                  <TextField
                                    select
                                    label="Cell type"
                                    value={cell.type || "input"}
                                    onChange={(event) =>
                                      handleCellTypeChange(
                                        rowIndex,
                                        columnIndex,
                                        event.target.value
                                      )
                                    }
                                  >
                                    <MenuItem value="input">Student input</MenuItem>
                                    <MenuItem value="readonly">Readonly text</MenuItem>
                                    <MenuItem value="dropdown">Dropdown</MenuItem>
                                  </TextField>

                                  {cell.type === "input" && (
                                    <Stack spacing={1}>
                                      <TextField
                                        label="Prefilled text (optional)"
                                        value={cell.prefill || ""}
                                        onChange={(event) =>
                                          dispatch({
                                            type: "UPDATE_CELL",
                                            rowIndex,
                                            columnIndex,
                                            updates: {
                                              prefill: event.target.value,
                                            },
                                          })
                                        }
                                        fullWidth
                                      />
                                      <TextField
                                        label="Expected answer (hidden from student)"
                                        value={cell.expectedAnswer || ""}
                                        onChange={(event) =>
                                          dispatch({
                                            type: "UPDATE_CELL",
                                            rowIndex,
                                            columnIndex,
                                            updates: {
                                              expectedAnswer: event.target.value,
                                            },
                                          })
                                        }
                                        fullWidth
                                      />
                                    </Stack>
                                  )}

                                  {cell.type === "readonly" && (
                                    <TextField
                                      label="Readonly text"
                                      value={cell.text || ""}
                                      onChange={(event) =>
                                        dispatch({
                                          type: "UPDATE_CELL",
                                          rowIndex,
                                          columnIndex,
                                          updates: { text: event.target.value },
                                        })
                                      }
                                      fullWidth
                                    />
                                  )}

                                  {cell.type === "dropdown" && (
                                    <Stack spacing={1}>
                                      <TextField
                                        select
                                        label="Select dropdown"
                                        value={cell.dropdownId || ""}
                                        onChange={(event) =>
                                          dispatch({
                                            type: "UPDATE_CELL",
                                            rowIndex,
                                            columnIndex,
                                            updates: { dropdownId: event.target.value },
                                          })
                                        }
                                        fullWidth
                                        helperText={
                                          dropdownOptions.length === 0
                                            ? "Create a dropdown from the sidebar."
                                            : undefined
                                        }
                                      >
                                        <MenuItem value="">
                                          {dropdownOptions.length
                                            ? "Select dropdown"
                                            : "No dropdowns available"}
                                        </MenuItem>
                                        {dropdownOptions.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>

                                      <TextField
                                        select
                                        label={
                                          dropdown?.allowMultiple
                                            ? "Correct options"
                                            : "Correct option"
                                        }
                                        value={
                                          dropdown?.allowMultiple
                                            ? selectedOptionIds
                                            : selectedOptionIds[0] || ""
                                        }
                                        onChange={(event) =>
                                          handleCorrectOptionChange(
                                            rowIndex,
                                            columnIndex,
                                            dropdown,
                                            event.target.value
                                          )
                                        }
                                        fullWidth
                                        disabled={
                                          !dropdown || !dropdown.options.length
                                        }
                                        SelectProps={
                                          dropdown?.allowMultiple
                                            ? {
                                                multiple: true,
                                                displayEmpty: true,
                                                renderValue: (selected) => {
                                                  if (
                                                    !Array.isArray(selected) ||
                                                    selected.length === 0
                                                  ) {
                                                    return "Select correct options";
                                                  }
                                                  return selected
                                                    .map((optionId) =>
                                                      getDropdownOptionLabel(
                                                        dropdown,
                                                        optionId
                                                      )
                                                    )
                                                    .join(", ");
                                                },
                                              }
                                            : {
                                                displayEmpty: true,
                                                renderValue: (selected) => {
                                                  if (!selected) {
                                                    return "Select correct option";
                                                  }
                                                  return getDropdownOptionLabel(
                                                    dropdown,
                                                    selected
                                                  );
                                                },
                                              }
                                        }
                                        helperText={
                                          !selectedOptionIds.length
                                            ? "Select the correct option(s)."
                                            : undefined
                                        }
                                      >
                                        {dropdown?.allowMultiple ? (
                                          <MenuItem disabled value="">
                                            Select correct options
                                          </MenuItem>
                                        ) : (
                                          <MenuItem disabled value="">
                                            Select correct option
                                          </MenuItem>
                                        )}
                                        {dropdown?.options.map((option) => (
                                          <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Stack>
                                  )}
                                </Stack>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Stack>
                    </Paper>
                  ))
                )}
              </Stack>
            </Stack>
          </Box>

        </Stack>
      </Paper>

    </>
  );

};

export default TableSectionEditor;
